import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Clock, Phone, User, CalendarDays, Settings, CheckCircle, XCircle, MessageSquare, Plus, Trash2, Send, Ban, CalendarOff, UtensilsCrossed, Users, Pizza, Percent, Hash, StickyNote, ShoppingBag, CreditCard, Loader2, Crown, Upload, Pencil, ChefHat, Package, Zap } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { SupportChatButton } from "@/components/SupportChat";
import { BookingChat } from "@/components/BookingChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending_payment: "bg-amber-100 text-amber-700 border-amber-300",
  pending_satispay: "bg-red-100 text-red-700 border-red-300",
  confirmed: "bg-emerald-100 text-emerald-700 border-emerald-300",
  cancelled: "bg-red-100 text-red-700 border-red-300",
  completed: "bg-blue-100 text-blue-700 border-blue-300",
};

const DEFAULT_RULES = Array.from({ length: 7 }, (_, i) => ({
  day_of_week: i,
  is_working: i < 5,
  start_time: "09:00",
  end_time: "17:00",
  slot_duration: 30,
}));

const MENU_CATEGORIES = ["restaurant", "pizzeria", "bar", "beach_club"];
const TABLE_CATEGORIES = ["restaurant", "hotel", "pizzeria", "bar", "beach_club"];

export default function BusinessDashboard() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [business, setBusiness] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState(DEFAULT_RULES);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [savingAvail, setSavingAvail] = useState(false);
  
  // AI Presets State
  const [presets, setPresets] = useState([]);
  const [newPresetTitle, setNewPresetTitle] = useState("");
  const [newPresetContent, setNewPresetContent] = useState("");
  const [addingPreset, setAddingPreset] = useState(false);
  
  // Chat State
  const [chatBooking, setChatBooking] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  
  // Blocked Slots State
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockDate, setBlockDate] = useState("");
  const [blockTime, setBlockTime] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [addingBlock, setAddingBlock] = useState(false);

  // Deposit edit state
  const [editDeposit, setEditDeposit] = useState(false);
  const [depositAmount, setDepositAmount] = useState(0);
  const [savingDeposit, setSavingDeposit] = useState(false);

  // Interactive slot grid state
  const [slotGridDate, setSlotGridDate] = useState(new Date());
  const [slotGridSlots, setSlotGridSlots] = useState([]);
  const [slotGridBooked, setSlotGridBooked] = useState([]);
  const [slotGridBlocked, setSlotGridBlocked] = useState([]);
  const [loadingSlotGrid, setLoadingSlotGrid] = useState(false);

  // Featured state
  const [featureLoading, setFeatureLoading] = useState(false);
  const [featuredImageUrl, setFeaturedImageUrl] = useState("");
  
  // Tables State (for restaurants/pubs)
  const [tables, setTables] = useState([]);
  const [totalTables, setTotalTables] = useState(0);
  const [newTotalTables, setNewTotalTables] = useState(0);
  const [savingTables, setSavingTables] = useState(false);
  const isRestaurant = TABLE_CATEGORIES.includes(business?.category);
  const hasMenu = MENU_CATEGORIES.includes(business?.category);
  const isPizzeria = business?.category === "pizzeria" || business?.category === "takeaway_pizza";

  // Menu Management State
  const [menuItems, setMenuItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ name: "", description: "", price: "", category: "", is_daily_special: false });
  const [showNewItemForm, setShowNewItemForm] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [dailyDiscount, setDailyDiscount] = useState(0);

  // WhatsApp state
  const [editWhatsapp, setEditWhatsapp] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);

  // Loyalty state
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [loyaltyThreshold, setLoyaltyThreshold] = useState(10);
  const [loyaltyRewardType, setLoyaltyRewardType] = useState("free_item");
  const [loyaltyRewardValue, setLoyaltyRewardValue] = useState(0);
  const [loyaltyRewardDesc, setLoyaltyRewardDesc] = useState("");
  const [savingLoyalty, setSavingLoyalty] = useState(false);
  const [loyaltyCustomers, setLoyaltyCustomers] = useState([]);

  useEffect(() => {
    fetchBusiness();
    // Trigger reminder check on load
    axios.post(`${API}/reminders/check`).catch(() => {});
    // Poll reminders every 5 minutes
    const interval = setInterval(() => {
      axios.post(`${API}/reminders/check`).catch(() => {});
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Handle featured payment return
  useEffect(() => {
    const featuredStatus = searchParams.get("featured");
    const featureId = searchParams.get("feature_id");
    if (featuredStatus === "success" && featureId) {
      // Confirm the featured status
      axios.post(`${API}/featured/confirm/${featureId}`, {}, { withCredentials: true })
        .then((res) => {
          toast.success("La tua attività è ora in evidenza per 3 giorni!");
        })
        .catch((err) => {
          toast.error(err.response?.data?.detail || "Errore nella conferma");
        })
        .finally(() => {
          // Clean URL params
          setSearchParams({});
        });
    } else if (featuredStatus === "cancelled") {
      toast.info("Pagamento annullato");
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Send heartbeat to indicate business is online
  useEffect(() => {
    if (!business) return;
    const sendHeartbeat = async () => {
      try { await axios.post(`${API}/presence/heartbeat`, {}, { withCredentials: true }); } catch {}
    };
    sendHeartbeat();
    const hb = setInterval(sendHeartbeat, 30000);
    return () => clearInterval(hb);
  }, [business]);

  const fetchBusiness = async () => {
    try {
      const res = await axios.get(`${API}/businesses/mine`, { withCredentials: true });
      setBusiness(res.data);
      setDepositAmount(res.data.deposit_amount || 0);
      setWhatsappNumber(res.data.whatsapp || "");
      // Load loyalty config
      setLoyaltyEnabled(res.data.loyalty_enabled || false);
      setLoyaltyThreshold(res.data.loyalty_threshold || 10);
      setLoyaltyRewardType(res.data.loyalty_reward_type || "free_item");
      setLoyaltyRewardValue(res.data.loyalty_reward_value || 0);
      setLoyaltyRewardDesc(res.data.loyalty_reward_description || "");
      fetchLoyaltyCustomers(res.data.business_id);
      fetchBookings();
      fetchAvailability(res.data.business_id);
      fetchPresets(res.data.business_id);
      fetchBlockedSlots();
      if (TABLE_CATEGORIES.includes(res.data.category)) {
        fetchTables(res.data.business_id);
      }
      if (MENU_CATEGORIES.includes(res.data.category)) {
        fetchMenu(res.data.business_id);
      }
    } catch (e) {
      if (e.response?.status === 404) {
        navigate("/business/register", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/bookings/business`, { withCredentials: true });
      setBookings(res.data);
    } catch {
      // ignore
    }
  };

  const fetchAvailability = async (bizId) => {
    try {
      const res = await axios.get(`${API}/availability/${bizId}`);
      if (res.data.length > 0) {
        const merged = DEFAULT_RULES.map((def) => {
          const found = res.data.find((r) => r.day_of_week === def.day_of_week);
          return found || def;
        });
        setAvailability(merged);
      }
    } catch {
      // ignore
    }
  };

  const fetchPresets = async (bizId) => {
    try {
      const res = await axios.get(`${API}/businesses/${bizId}/presets`, { withCredentials: true });
      setPresets(res.data);
    } catch {
      // ignore
    }
  };

  const addPreset = async () => {
    if (!newPresetTitle.trim() || !newPresetContent.trim()) {
      toast.error("Compila titolo e contenuto");
      return;
    }
    setAddingPreset(true);
    try {
      await axios.post(
        `${API}/businesses/${business.business_id}/presets`,
        { title: newPresetTitle, content: newPresetContent },
        { withCredentials: true }
      );
      setNewPresetTitle("");
      setNewPresetContent("");
      fetchPresets(business.business_id);
      toast.success("Template aggiunto!");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setAddingPreset(false);
    }
  };

  const deletePreset = async (presetId) => {
    try {
      await axios.delete(
        `${API}/businesses/${business.business_id}/presets/${presetId}`,
        { withCredentials: true }
      );
      fetchPresets(business.business_id);
      toast.success("Template eliminato");
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const fetchBlockedSlots = async () => {
    try {
      const res = await axios.get(`${API}/blocked-slots`, { withCredentials: true });
      setBlockedSlots(res.data);
    } catch {
      // ignore
    }
  };

  const addBlockedSlot = async () => {
    if (!blockDate) {
      toast.error("Seleziona una data");
      return;
    }
    setAddingBlock(true);
    try {
      await axios.post(
        `${API}/blocked-slots`,
        {
          date: blockDate,
          time_slot: blockTime || null,
          reason: blockReason,
        },
        { withCredentials: true }
      );
      setBlockDate("");
      setBlockTime("");
      setBlockReason("");
      fetchBlockedSlots();
      toast.success("Slot bloccato!");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setAddingBlock(false);
    }
  };

  const removeBlockedSlot = async (blockId) => {
    try {
      await axios.delete(`${API}/blocked-slots/${blockId}`, { withCredentials: true });
      fetchBlockedSlots();
      toast.success("Slot sbloccato");
    } catch {
      toast.error(t("common.error"));
    }
  };

  // Tables functions
  const fetchTables = async (bizId) => {
    try {
      const res = await axios.get(`${API}/businesses/${bizId}/tables`);
      setTables(res.data.tables || []);
      setTotalTables(res.data.total || 0);
      setNewTotalTables(res.data.total || 0);
    } catch {
      // ignore
    }
  };

  const setupTables = async () => {
    if (newTotalTables < 1 || newTotalTables > 100) {
      toast.error("Numero tavoli deve essere tra 1 e 100");
      return;
    }
    setSavingTables(true);
    try {
      await axios.post(
        `${API}/businesses/${business.business_id}/tables/setup`,
        { total_tables: newTotalTables },
        { withCredentials: true }
      );
      fetchTables(business.business_id);
      toast.success(`${newTotalTables} tavoli configurati!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSavingTables(false);
    }
  };

  const toggleTableStatus = async (tableNumber, currentStatus) => {
    const newStatus = currentStatus === "available" ? "occupied" : "available";
    try {
      await axios.put(
        `${API}/businesses/${business.business_id}/tables/${tableNumber}`,
        { table_number: tableNumber, status: newStatus },
        { withCredentials: true }
      );
      fetchTables(business.business_id);
      if (newStatus === "available") {
        toast.success(`Tavolo ${tableNumber} liberato! Clienti notificati.`);
      } else {
        toast.success(`Tavolo ${tableNumber} occupato`);
      }
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  // Loyalty functions
  const fetchLoyaltyCustomers = async (bizId) => {
    try {
      const res = await axios.get(`${API}/businesses/${bizId}/loyalty/all`, { withCredentials: true });
      setLoyaltyCustomers(res.data || []);
    } catch {}
  };

  const saveLoyalty = async () => {
    setSavingLoyalty(true);
    try {
      await axios.put(`${API}/businesses/${business.business_id}/loyalty`, {
        enabled: loyaltyEnabled,
        reward_threshold: loyaltyThreshold,
        reward_type: loyaltyRewardType,
        reward_value: loyaltyRewardValue,
        reward_description: loyaltyRewardDesc,
      }, { withCredentials: true });
      toast.success("Programma fedelta salvato!");
    } catch (err) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSavingLoyalty(false);
    }
  };

  const updateTableSeats = async (tableNumber, seats) => {
    try {
      await axios.put(`${API}/businesses/${business.business_id}/tables/${tableNumber}/seats`, { seats }, { withCredentials: true });
      fetchTables(business.business_id);
      toast.success(`Tavolo ${tableNumber}: ${seats} posti`);
    } catch {
      toast.error("Errore aggiornamento posti");
    }
  };


  // Menu functions
  const fetchMenu = async (bizId) => {
    try {
      const res = await axios.get(`${API}/businesses/${bizId}/menu`);
      setMenuItems(res.data.items || []);
      setDailyDiscount(res.data.daily_discount || 0);
    } catch {
      // ignore
    }
  };

  const addMenuItem = async () => {
    if (!newItem.name || !newItem.price || parseFloat(newItem.price) <= 0) {
      toast.error("Inserisci nome e prezzo");
      return;
    }
    setAddingItem(true);
    try {
      await axios.post(
        `${API}/businesses/${business.business_id}/menu`,
        { ...newItem, price: parseFloat(newItem.price) },
        { withCredentials: true }
      );
      setNewItem({ name: "", description: "", price: "", category: "", is_daily_special: false });
      setEditingItem(null);
      fetchMenu(business.business_id);
      toast.success("Prodotto aggiunto!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setAddingItem(false);
    }
  };

  const deleteMenuItem = async (itemId) => {
    if (!window.confirm("Eliminare questo piatto?")) return;
    try {
      await axios.delete(`${API}/businesses/${business.business_id}/menu/${itemId}`, { withCredentials: true });
      fetchMenu(business.business_id);
      toast.success("Prodotto eliminato");
    } catch {
      toast.error("Errore");
    }
  };

  const saveDailyDiscount = async () => {
    try {
      await axios.put(
        `${API}/businesses/${business.business_id}/daily-discount`,
        { discount: dailyDiscount },
        { withCredentials: true }
      );
      toast.success(`Sconto ${dailyDiscount}% applicato!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const loadMenuPresets = async () => {
    setLoadingPresets(true);
    try {
      const res = await axios.post(`${API}/businesses/${business.business_id}/menu/load-presets`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchMenu(business.business_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setLoadingPresets(false); }
  };

  const updateOrderStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/order-status`, { order_status: newStatus }, { withCredentials: true });
      toast.success(newStatus === "preparing" ? "In lavorazione!" : newStatus === "ready" ? "Pronto!" : "Aggiornato");
      fetchBusiness();
    } catch { toast.error("Errore"); }
  };

  // Deposit management
  const saveDeposit = async () => {
    setSavingDeposit(true);
    try {
      await axios.put(
        `${API}/businesses/${business.business_id}`,
        { deposit_amount: depositAmount },
        { withCredentials: true }
      );
      setBusiness(prev => ({ ...prev, deposit_amount: depositAmount }));
      setEditDeposit(false);
      toast.success("Cauzione aggiornata!");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSavingDeposit(false);
    }
  };

  // WhatsApp management
  const saveWhatsapp = async () => {
    setSavingWhatsapp(true);
    try {
      await axios.put(
        `${API}/businesses/${business.business_id}`,
        { whatsapp: whatsappNumber },
        { withCredentials: true }
      );
      setBusiness(prev => ({ ...prev, whatsapp: whatsappNumber }));
      setEditWhatsapp(false);
      toast.success("Numero WhatsApp salvato!");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setSavingWhatsapp(false);
    }
  };

  // Featured / In Evidenza
  const handleFeatureCheckout = async () => {
    setFeatureLoading(true);
    try {
      const res = await axios.post(
        `${API}/featured/checkout`,
        { image_url: featuredImageUrl || "", origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setFeatureLoading(false);
    }
  };

  // Slot grid management
  const fetchSlotGrid = async (date) => {
    if (!business) return;
    const y = date.getFullYear(), m = String(date.getMonth()+1).padStart(2,"0"), d = String(date.getDate()).padStart(2,"0");
    const dateStr = `${y}-${m}-${d}`;
    setLoadingSlotGrid(true);
    try {
      const res = await axios.get(`${API}/availability/${business.business_id}/${dateStr}`);
      setSlotGridSlots(res.data.slots || []);
      setSlotGridBooked(res.data.booked || []);
      setSlotGridBlocked(res.data.blocked || []);
    } catch {
      setSlotGridSlots([]);
    } finally {
      setLoadingSlotGrid(false);
    }
  };

  useEffect(() => {
    if (business) fetchSlotGrid(slotGridDate);
  }, [business, slotGridDate]);

  const toggleSlotBlock = async (slot) => {
    const y = slotGridDate.getFullYear(), m = String(slotGridDate.getMonth()+1).padStart(2,"0"), d = String(slotGridDate.getDate()).padStart(2,"0");
    const dateStr = `${y}-${m}-${d}`;
    try {
      const res = await axios.post(
        `${API}/blocked-slots/toggle`,
        { date: dateStr, time_slot: slot },
        { withCredentials: true }
      );
      if (res.data.action === "blocked") {
        setSlotGridBlocked(prev => [...prev, slot]);
        toast.success(`${slot} bloccato`);
      } else {
        setSlotGridBlocked(prev => prev.filter(s => s !== slot));
        toast.success(`${slot} sbloccato`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    }
  };

  // ============ MENU MANAGEMENT (handled above) ============

  const updateRule = (dayIndex, field, value) => {
    setAvailability((prev) =>
      prev.map((r) => (r.day_of_week === dayIndex ? { ...r, [field]: value } : r))
    );
  };

  const saveAvailability = async () => {
    setSavingAvail(true);
    try {
      await axios.post(`${API}/availability`, availability, { withCredentials: true });
      toast.success(t("common.save") + "!");
    } catch {
      toast.error(t("common.error"));
    } finally {
      setSavingAvail(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(
        `${API}/bookings/${bookingId}/status`,
        { status },
        { withCredentials: true }
      );
      fetchBookings();
      toast.success(t(`status.${status}`));
    } catch {
      toast.error(t("common.error"));
    }
  };

  const confirmSatispay = async (bookingId) => {
    if (!window.confirm("Confermi di aver ricevuto il pagamento Satispay?")) return;
    try {
      await axios.put(`${API}/bookings/${bookingId}/confirm-satispay`, {}, { withCredentials: true });
      fetchBookings();
      toast.success("Pagamento Satispay confermato!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella conferma");
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-[#E2725B] border-t-transparent rounded-full" />
      </div>
    );
  }

  const _todayD = new Date();
  const today = `${_todayD.getFullYear()}-${String(_todayD.getMonth()+1).padStart(2,"0")}-${String(_todayD.getDate()).padStart(2,"0")}`;
  const todayBookings = bookings.filter((b) => b.date === today);
  const _selD = selectedDate;
  const selectedDateStr = `${_selD.getFullYear()}-${String(_selD.getMonth()+1).padStart(2,"0")}-${String(_selD.getDate()).padStart(2,"0")}`;
  const filteredBookings = bookings.filter((b) => b.date === selectedDateStr);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar />
      <div className="pt-28 pb-16 px-6 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start justify-between mb-12">
            <div>
              <h1
                className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-[#2A1B14]"
                data-testid="business-dashboard-title"
              >
                {business?.name}
              </h1>
              <div className="flex items-center gap-3 mt-3">
                <Badge className="rounded-full border border-[rgba(226,114,91,0.2)] bg-[#E2725B]/5 text-[#E2725B] text-xs px-3 py-1 font-bold">
                  {t(`cat.${business?.category}`)}
                </Badge>
                {business?.verified ? (
                  <Badge className="rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs px-3 py-1 font-bold">
                    Verified
                  </Badge>
                ) : (
                  <Badge className="rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-xs px-3 py-1 font-bold">
                    Pending Verification
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="p-6 bg-white rounded-3xl border border-[rgba(226,114,91,0.08)] shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300">
              <p className="text-xs uppercase tracking-widest text-[#6A564A] mb-2">{t("biz.today")}</p>
              <p className="font-heading text-3xl font-black gradient-text">{todayBookings.length}</p>
            </div>
            <div className="p-6 bg-white rounded-3xl border border-[rgba(226,114,91,0.08)] shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300">
              <p className="text-xs uppercase tracking-widest text-[#6A564A] mb-2">{t("biz.total")}</p>
              <p className="font-heading text-3xl font-black text-[#2A1B14]">{bookings.length}</p>
            </div>
            <div className="p-6 bg-white rounded-3xl border border-[rgba(226,114,91,0.08)] shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300">
              <p className="text-xs uppercase tracking-widest text-[#6A564A] mb-2">{t("status.confirmed")}</p>
              <p className="font-heading text-3xl font-black text-emerald-600">
                {bookings.filter((b) => b.status === "confirmed").length}
              </p>
            </div>
            <div
              className="p-6 bg-white rounded-3xl border-2 border-[#F39C12]/20 shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group"
              onClick={() => setEditDeposit(!editDeposit)}
              data-testid="deposit-card"
            >
              <p className="text-xs uppercase tracking-widest text-[#F39C12] mb-2 flex items-center gap-1 font-bold">
                <CreditCard className="h-3 w-3" strokeWidth={1.5} />
                Cauzione
              </p>
              {editDeposit ? (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Input
                    type="number"
                    step="0.50"
                    min="0"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(parseFloat(e.target.value) || 0)}
                    className="rounded-xl border-[#F39C12]/30 h-10 w-20 text-lg font-bold"
                    data-testid="deposit-amount-input"
                  />
                  <span className="text-sm text-[#F39C12] font-bold">€</span>
                  <Button
                    size="sm"
                    onClick={saveDeposit}
                    disabled={savingDeposit}
                    className="rounded-full bg-gradient-to-r from-[#E2725B] to-[#F39C12] text-white border-0 h-10 px-3 hover:scale-105 active:scale-95 transition-transform"
                    data-testid="save-deposit-btn"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="font-heading text-3xl font-black text-[#F39C12]">
                    {(business?.deposit_amount || 0).toFixed(2)}€
                  </p>
                  <p className="text-[10px] text-[#6A564A] mt-1 group-hover:text-[#E2725B] transition-colors">
                    + 1€ piattaforma = {((business?.deposit_amount || 0) + 1).toFixed(2)}€ totale
                  </p>
                </div>
              )}
            </div>
            {/* WhatsApp Contact Card */}
            <div
              className="p-6 bg-white rounded-3xl border-2 border-emerald-200/50 shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group"
              onClick={() => setEditWhatsapp(!editWhatsapp)}
              data-testid="whatsapp-card"
            >
              <p className="text-xs uppercase tracking-widest text-emerald-600 mb-2 flex items-center gap-1 font-bold">
                <Phone className="h-3 w-3" strokeWidth={1.5} />
                WhatsApp
              </p>
              {editWhatsapp ? (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <Input
                    type="tel"
                    placeholder="+39 333 1234567"
                    value={whatsappNumber}
                    onChange={(e) => setWhatsappNumber(e.target.value)}
                    className="rounded-xl border-emerald-300 h-10 w-40 text-sm font-bold"
                    data-testid="whatsapp-input"
                  />
                  <Button
                    size="sm"
                    onClick={saveWhatsapp}
                    disabled={savingWhatsapp}
                    className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 h-10 px-3 hover:scale-105 active:scale-95 transition-transform"
                    data-testid="save-whatsapp-btn"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div>
                  <p className="font-heading text-lg font-black text-emerald-600">
                    {business?.whatsapp || "Non impostato"}
                  </p>
                  <p className="text-[10px] text-[#6A564A] mt-1 group-hover:text-emerald-600 transition-colors">
                    Clicca per modificare
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Featured Banner */}
          <div className="mb-8 bg-[#09090B] border-2 border-[#09090B] rounded-2xl p-6 shadow-brutal" data-testid="featured-banner">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#FFE600] border-2 border-[#09090B] flex items-center justify-center shadow-brutal-sm">
                  <Crown className="h-6 w-6 text-[#09090B]" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">Metti in Evidenza la tua Attivita</h3>
                  <p className="text-white/50 text-sm">Appari nella homepage per 3 giorni — solo 1€</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Image upload */}
                <label className="cursor-pointer px-4 py-2.5 rounded-xl border-2 border-white/20 text-white text-sm font-bold hover:bg-white/10 transition-colors flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  {featuredImageUrl ? "Cambia Foto" : "Aggiungi Foto"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append("file", file);
                      try {
                        const res = await axios.post(`${API}/featured/upload-image`, formData, { withCredentials: true });
                        setFeaturedImageUrl(res.data.image_url);
                        toast.success("Immagine caricata!");
                      } catch (err) {
                        toast.error("Errore nel caricamento immagine");
                      }
                    }}
                    data-testid="featured-image-upload"
                  />
                </label>
                <Button
                  onClick={handleFeatureCheckout}
                  disabled={featureLoading}
                  className="rounded-xl px-5 py-5 bg-[#FFE600] text-[#09090B] font-bold border-2 border-[#09090B] shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all duration-200 shrink-0"
                  data-testid="feature-checkout-btn"
                >
                  {featureLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
                  In Evidenza — 1€
                </Button>
              </div>
            </div>
            {featuredImageUrl && (
              <div className="mt-4 rounded-xl border-2 border-white/20 overflow-hidden h-32 w-48">
                <img src={featuredImageUrl} alt="Anteprima" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <Tabs defaultValue={isPizzeria ? "orders" : "bookings"} className="w-full">
            <TabsList className="rounded-full border border-[rgba(226,114,91,0.1)] bg-white/80 backdrop-blur-sm h-12 mb-8 p-1 shadow-warm flex-wrap">
              {isPizzeria ? (
                <TabsTrigger
                  value="orders"
                  className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                  data-testid="tab-biz-orders"
                >
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  {t("pizzeria.orders_today")}
                </TabsTrigger>
              ) : (
                <TabsTrigger
                  value="bookings"
                  className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                  data-testid="tab-biz-bookings"
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {t("biz.bookings")}
                </TabsTrigger>
              )}
              <TabsTrigger
                value="availability"
                className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                data-testid="tab-biz-availability"
              >
                <Clock className="mr-2 h-4 w-4" />
                {t("biz.availability")}
              </TabsTrigger>
              <TabsTrigger
                value="slotgrid"
                className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                data-testid="tab-biz-slotgrid"
              >
                <Settings className="mr-2 h-4 w-4" />
                Gestione Slot
              </TabsTrigger>
              <TabsTrigger
                value="templates"
                className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                data-testid="tab-biz-templates"
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                {t("ai.presets.manage")}
              </TabsTrigger>
              <TabsTrigger
                value="blocked"
                className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                data-testid="tab-biz-blocked"
              >
                <Ban className="mr-2 h-4 w-4" />
                {t("block.title")}
              </TabsTrigger>
              {isRestaurant && (
                <TabsTrigger
                  value="tables"
                  className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                  data-testid="tab-biz-tables"
                >
                  <UtensilsCrossed className="mr-2 h-4 w-4" />
                  {t("tables.title")}
                </TabsTrigger>
              )}
              {hasMenu && (
                <TabsTrigger
                  value="menu"
                  className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                  data-testid="tab-biz-menu"
                >
                  <Pizza className="mr-2 h-4 w-4" />
                  {t("menu.title")}
                </TabsTrigger>
              )}
              <TabsTrigger
                value="loyalty"
                className="rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#E2725B] data-[state=active]:to-[#F39C12] data-[state=active]:text-white data-[state=active]:shadow-md px-5 transition-all duration-200"
                data-testid="tab-biz-loyalty"
              >
                <Crown className="mr-2 h-4 w-4" />
                Fedelta
              </TabsTrigger>
            </TabsList>

            {/* Pizzeria Orders Tab — Kitchen Ticket Style */}
            {isPizzeria && (
              <TabsContent value="orders">
                <div>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
                      <ShoppingBag className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800 text-lg">{t("pizzeria.orders_today")}</h3>
                      <p className="text-xs text-slate-400">{today} - {todayBookings.length} ordini</p>
                    </div>
                  </div>
                  {todayBookings.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-12 text-center">
                      <Pizza className="h-16 w-16 text-orange-200 mx-auto mb-4" />
                      <p className="text-slate-400">{t("pizzeria.no_orders")}</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {todayBookings
                        .sort((a, b) => a.time_slot.localeCompare(b.time_slot))
                        .map((b, index) => (
                        <div
                          key={b.booking_id}
                          className="bg-white rounded-3xl border border-[rgba(226,114,91,0.1)] shadow-warm hover:shadow-warm-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                          data-testid={`order-ticket-${b.booking_id}`}
                        >
                          {/* Ticket header */}
                          <div className="bg-gradient-to-r from-[#E2725B] to-[#F39C12] px-5 py-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Hash className="h-5 w-5 text-white" />
                              <span className="font-black text-2xl text-white">{index + 1}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-white font-bold text-lg">{b.time_slot}</span>
                              <p className="text-orange-100 text-[10px]">+10 min intervallo</p>
                            </div>
                          </div>
                          {/* Ticket body */}
                          <div className="p-5 space-y-3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-orange-500" />
                              <span className="font-bold text-slate-800">{b.customer_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Phone className="h-3.5 w-3.5" />
                              <span>{b.customer_phone}</span>
                              {b.customer_phone && (
                                <a
                                  href={`https://wa.me/${b.customer_phone.replace(/[^0-9]/g, '')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#25D366] text-white rounded-full text-[10px] font-bold hover:bg-[#128C7E] transition-colors"
                                  data-testid={`ticket-whatsapp-${b.booking_id}`}
                                >
                                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                  WA
                                </a>
                              )}
                            </div>
                            {b.customer_notes && (
                              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <StickyNote className="h-3.5 w-3.5 text-amber-600" />
                                  <span className="text-[10px] uppercase tracking-widest text-amber-600 font-bold">Note</span>
                                </div>
                                <p className="text-sm text-slate-700">{b.customer_notes}</p>
                              </div>
                            )}
                            {/* Cart Items */}
                            {b.cart_items && b.cart_items.length > 0 && (
                              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid={`ticket-cart-${b.booking_id}`}>
                                <div className="flex items-center gap-1.5 mb-2">
                                  <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Ordine</span>
                                </div>
                                <div className="space-y-1">
                                  {b.cart_items.map((ci, idx) => (
                                    <div key={idx} className="flex justify-between text-sm">
                                      <span className="text-slate-700">{ci.quantity}x {ci.name}</span>
                                      <span className="font-bold text-emerald-700">{(ci.price * ci.quantity).toFixed(2)}€</span>
                                    </div>
                                  ))}
                                </div>
                                {b.cart_total > 0 && (
                                  <div className="border-t border-emerald-300 mt-2 pt-1 flex justify-between text-sm font-black">
                                    <span className="text-emerald-800">Totale</span>
                                    <span className="text-emerald-800">{b.cart_total.toFixed(2)}€</span>
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="flex items-center justify-between pt-2 border-t border-orange-100">
                              <Badge className={`rounded-full border text-xs px-3 py-1 ${STATUS_COLORS[b.status] || ""}`}>
                                {t(`status.${b.status}`)}
                              </Badge>
                              <div className="flex items-center gap-2">
                                {/* Satispay Confirm Button */}
                                {b.status === "pending_satispay" && (
                                  <button
                                    onClick={() => confirmSatispay(b.booking_id)}
                                    className="px-3 py-1.5 rounded-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-xs font-bold flex items-center gap-1 transition-all shadow-sm"
                                    data-testid={`confirm-satispay-${b.booking_id}`}
                                  >
                                    <Zap className="h-3.5 w-3.5" />
                                    Conferma Satispay
                                  </button>
                                )}
                                {/* Order Status Buttons */}
                                {b.status === "confirmed" && (
                                  <>
                                    {(!b.order_status || b.order_status === "pending") && (
                                      <button
                                        onClick={() => updateOrderStatus(b.booking_id, "preparing")}
                                        className="px-3 py-1.5 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold flex items-center gap-1 transition-colors"
                                        data-testid={`btn-preparing-${b.booking_id}`}
                                      >
                                        <ChefHat className="h-3.5 w-3.5" />
                                        In Lavorazione
                                      </button>
                                    )}
                                    {b.order_status === "preparing" && (
                                      <button
                                        onClick={() => updateOrderStatus(b.booking_id, "ready")}
                                        className="px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold flex items-center gap-1 animate-pulse transition-colors"
                                        data-testid={`btn-ready-${b.booking_id}`}
                                      >
                                        <Package className="h-3.5 w-3.5" />
                                        Pronto!
                                      </button>
                                    )}
                                    {b.order_status === "ready" && (
                                      <Badge className="rounded-full bg-emerald-100 text-emerald-700 border-emerald-300 text-xs px-3 py-1">
                                        <CheckCircle className="h-3 w-3 mr-1" /> Pronto
                                      </Badge>
                                    )}
                                  </>
                                )}
                                <button
                                  onClick={() => {
                                    setChatBooking(b);
                                    setChatOpen(true);
                                  }}
                                  className="p-1.5 hover:bg-blue-50 transition-colors rounded-lg border border-slate-200"
                                  data-testid={`chat-${b.booking_id}`}
                                >
                                  <Send className="h-4 w-4 text-blue-500" />
                                </button>
                                {b.status === "confirmed" && (
                                  <>
                                    <button
                                      onClick={() => updateBookingStatus(b.booking_id, "completed")}
                                      className="p-1.5 hover:bg-emerald-50 transition-colors rounded-lg"
                                      data-testid={`complete-${b.booking_id}`}
                                    >
                                      <CheckCircle className="h-4 w-4 text-emerald-600" />
                                    </button>
                                    <button
                                      onClick={() => updateBookingStatus(b.booking_id, "cancelled")}
                                      className="p-1.5 hover:bg-red-50 transition-colors rounded-lg"
                                      data-testid={`cancel-${b.booking_id}`}
                                    >
                                      <XCircle className="h-4 w-4 text-red-500" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          {/* Ticket tear-off effect */}
                          <div className="h-3 bg-orange-50 border-t-2 border-dashed border-orange-200" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>
            )}

            {/* Bookings Tab */}
            <TabsContent value="bookings">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-4">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => d && setSelectedDate(d)}
                    className="w-full"
                    data-testid="business-calendar"
                  />
                </div>
                <div className="lg:col-span-2">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">
                    {selectedDateStr} - {filteredBookings.length} {t("biz.bookings").toLowerCase()}
                  </h3>
                  {filteredBookings.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-8 text-center">
                      <p className="text-sm text-slate-400">{t("biz.no_bookings")}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredBookings.map((b) => (
                        <div
                          key={b.booking_id}
                          className="p-5 bg-white rounded-2xl border border-orange-100 shadow-sm"
                          data-testid={`biz-booking-${b.booking_id}`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <User className="h-4 w-4 text-orange-500" />
                                <span className="font-bold text-slate-800">{b.customer_name}</span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Phone className="inline h-3.5 w-3.5" />
                                  {b.customer_phone}
                                  {b.customer_phone && (
                                    <a
                                      href={`https://wa.me/${b.customer_phone.replace(/[^0-9]/g, '')}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-0.5 ml-1 px-1.5 py-0.5 bg-[#25D366] text-white rounded-full text-[9px] font-bold hover:bg-[#128C7E] transition-colors"
                                    >
                                      WA
                                    </a>
                                  )}
                                </span>
                                <span>
                                  <Clock className="inline h-3.5 w-3.5 mr-1" />
                                  {b.time_slot}
                                </span>
                              </div>
                              {b.customer_notes && (
                                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-slate-600">
                                  <MessageSquare className="inline h-3 w-3 mr-1 text-amber-500" />
                                  {b.customer_notes}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={`rounded-full border text-xs px-3 py-1 ${STATUS_COLORS[b.status] || ""}`}>
                                {t(`status.${b.status}`)}
                              </Badge>
                              <button
                                onClick={() => {
                                  setChatBooking(b);
                                  setChatOpen(true);
                                }}
                                className="p-1.5 hover:bg-blue-50 transition-colors rounded-lg border border-slate-200"
                                data-testid={`chat-${b.booking_id}`}
                                title={t("chat.title")}
                              >
                                <Send className="h-4 w-4 text-blue-500" />
                              </button>
                              {b.status === "confirmed" && (
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => updateBookingStatus(b.booking_id, "completed")}
                                    className="p-1.5 hover:bg-emerald-50 transition-colors rounded-lg"
                                    data-testid={`complete-${b.booking_id}`}
                                  >
                                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                                  </button>
                                  <button
                                    onClick={() => updateBookingStatus(b.booking_id, "cancelled")}
                                    className="p-1.5 hover:bg-red-50 transition-colors rounded-lg"
                                    data-testid={`cancel-${b.booking_id}`}
                                  >
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Availability Tab */}
            <TabsContent value="availability">
              <div className="bg-white rounded-2xl border border-orange-100 shadow-sm overflow-hidden">
                {availability.map((rule, i) => (
                  <div
                    key={rule.day_of_week}
                    className={`p-5 ${i < 6 ? "border-b border-orange-50" : ""}`}
                    data-testid={`avail-day-${rule.day_of_week}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex items-center gap-3 w-40">
                        <Switch
                          checked={rule.is_working}
                          onCheckedChange={(v) => updateRule(rule.day_of_week, "is_working", v)}
                          data-testid={`avail-switch-${rule.day_of_week}`}
                        />
                        <span className={`text-sm font-medium ${rule.is_working ? "text-slate-800" : "text-slate-400 line-through"}`}>
                          {t(`avail.day.${rule.day_of_week}`)}
                        </span>
                      </div>
                      {rule.is_working && (
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-slate-400">{t("avail.start")}</label>
                            <Input
                              type="time"
                              value={rule.start_time}
                              onChange={(e) => updateRule(rule.day_of_week, "start_time", e.target.value)}
                              className="rounded-xl w-[120px] h-9 border-orange-200"
                              data-testid={`avail-start-${rule.day_of_week}`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-slate-400">{t("avail.end")}</label>
                            <Input
                              type="time"
                              value={rule.end_time}
                              onChange={(e) => updateRule(rule.day_of_week, "end_time", e.target.value)}
                              className="rounded-xl w-[120px] h-9 border-orange-200"
                              data-testid={`avail-end-${rule.day_of_week}`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-slate-400">{t("avail.duration")}</label>
                            <Input
                              type="number"
                              value={rule.slot_duration}
                              onChange={(e) => updateRule(rule.day_of_week, "slot_duration", parseInt(e.target.value) || 30)}
                              className="rounded-xl w-[80px] h-9 border-orange-200"
                              min={10}
                              max={120}
                              data-testid={`avail-duration-${rule.day_of_week}`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Button
                  data-testid="save-availability-button"
                  onClick={saveAvailability}
                  disabled={savingAvail}
                  className="rounded-xl px-8 py-5 bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-200 border-0 transition-all duration-200"
                >
                  {savingAvail ? t("common.loading") : t("avail.save")}
                </Button>
              </div>
            </TabsContent>

            {/* AI Templates Tab */}
            <TabsContent value="templates">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("ai.presets.add")}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">{t("ai.presets.title")}</label>
                      <Input
                        value={newPresetTitle}
                        onChange={(e) => setNewPresetTitle(e.target.value)}
                        placeholder="es. Taglio e Barba"
                        className="rounded-xl border-orange-200"
                        data-testid="new-preset-title"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">{t("ai.presets.content")}</label>
                      <Textarea
                        value={newPresetContent}
                        onChange={(e) => setNewPresetContent(e.target.value)}
                        placeholder="es. Vorrei prenotare un taglio di capelli..."
                        className="rounded-xl border-orange-200 min-h-[100px]"
                        data-testid="new-preset-content"
                      />
                    </div>
                    <Button
                      onClick={addPreset}
                      disabled={addingPreset || !newPresetTitle.trim() || !newPresetContent.trim()}
                      className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-200 border-0 w-full"
                      data-testid="add-preset-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {addingPreset ? t("common.loading") : t("ai.presets.add")}
                    </Button>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("ai.presets")} ({presets.length})</h3>
                  {presets.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">{t("ai.presets.empty")}</p>
                  ) : (
                    <div className="space-y-3">
                      {presets.map((preset) => (
                        <div
                          key={preset.preset_id}
                          className="p-4 border border-orange-100 rounded-xl hover:border-orange-300 transition-colors"
                          data-testid={`preset-item-${preset.preset_id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <p className="font-bold text-sm text-slate-800 mb-1">{preset.title}</p>
                              <p className="text-xs text-slate-400">{preset.content}</p>
                            </div>
                            <button onClick={() => deletePreset(preset.preset_id)} className="p-1.5 hover:bg-red-50 transition-colors rounded-lg" data-testid={`delete-preset-${preset.preset_id}`}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Blocked Slots Tab */}
            <TabsContent value="blocked">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("block.add")}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">{t("block.date")} *</label>
                      <Input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} min={today} className="rounded-xl border-orange-200" data-testid="block-date-input" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">{t("block.time")}</label>
                      <Input type="time" value={blockTime} onChange={(e) => setBlockTime(e.target.value)} className="rounded-xl border-orange-200" data-testid="block-time-input" />
                      <p className="text-xs text-slate-400 mt-1">Lascia vuoto per bloccare l'intera giornata</p>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">{t("block.reason")}</label>
                      <Input value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="es. Ferie, impegno personale..." className="rounded-xl border-orange-200" data-testid="block-reason-input" />
                    </div>
                    <Button onClick={addBlockedSlot} disabled={addingBlock || !blockDate} className="rounded-xl bg-red-500 text-white hover:bg-red-600 border-0 w-full" data-testid="add-block-btn">
                      <Ban className="h-4 w-4 mr-2" />
                      {addingBlock ? t("common.loading") : t("block.add")}
                    </Button>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("block.title")} ({blockedSlots.length})</h3>
                  {blockedSlots.length === 0 ? (
                    <div className="text-center py-8">
                      <CalendarOff className="h-12 w-12 text-orange-200 mx-auto mb-3" />
                      <p className="text-sm text-slate-400">{t("block.empty")}</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {blockedSlots.map((block) => (
                        <div key={block.block_id} className="p-4 border border-red-100 bg-red-50 rounded-xl" data-testid={`block-item-${block.block_id}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <CalendarDays className="h-4 w-4 text-red-500" />
                                <p className="font-bold text-sm text-slate-800">
                                  {new Date(block.date).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}
                                </p>
                                <Badge className="rounded-full border border-red-300 bg-red-100 text-red-700 text-xs">
                                  {block.time_slot || t("block.entire_day")}
                                </Badge>
                              </div>
                              {block.reason && <p className="text-xs text-slate-500">{block.reason}</p>}
                            </div>
                            <button onClick={() => removeBlockedSlot(block.block_id)} className="p-1.5 hover:bg-white transition-colors rounded-lg" data-testid={`unblock-${block.block_id}`}>
                              <XCircle className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Interactive Slot Grid — Click to block/unblock */}
            <TabsContent value="slotgrid">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-4">
                  <Calendar
                    mode="single"
                    selected={slotGridDate}
                    onSelect={(d) => d && setSlotGridDate(d)}
                    className="w-full"
                    data-testid="slot-grid-calendar"
                  />
                </div>
                <div className="lg:col-span-2">
                  <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xs uppercase tracking-widest text-slate-400">
                        {slotGridDate.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                      </h3>
                      <div className="flex items-center gap-4 text-xs">
                        <span className="flex items-center gap-1.5">
                          <span className="w-3 h-3 bg-emerald-400 border border-emerald-500 rounded-full" />
                          Disponibile
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-3 h-3 bg-red-400 border border-red-500 rounded-full" />
                          Bloccato
                        </span>
                        <span className="flex items-center gap-1.5">
                          <span className="w-3 h-3 bg-blue-400 border border-blue-500 rounded-full" />
                          Prenotato
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mb-4">Clicca su uno slot per bloccarlo/sbloccarlo</p>
                    {loadingSlotGrid ? (
                      <div className="flex justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                      </div>
                    ) : slotGridSlots.length === 0 ? (
                      <div className="text-center py-12">
                        <CalendarOff className="h-12 w-12 text-orange-200 mx-auto mb-3" />
                        <p className="text-sm text-slate-400">Nessuno slot disponibile per questo giorno</p>
                        <p className="text-xs text-slate-300 mt-1">Configura gli orari nella tab "Disponibilità"</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                        {slotGridSlots.map((slot) => {
                          const isBooked = slotGridBooked.includes(slot);
                          const isBlocked = slotGridBlocked.includes(slot);
                          return (
                            <button
                              key={slot}
                              onClick={() => !isBooked && toggleSlotBlock(slot)}
                              disabled={isBooked}
                              className={`
                                p-2.5 text-xs font-bold rounded-xl border-2 transition-all duration-200 hover:scale-105
                                ${isBooked
                                  ? "bg-blue-50 border-blue-300 text-blue-500 cursor-not-allowed"
                                  : isBlocked
                                    ? "bg-red-100 border-red-400 text-red-600 hover:bg-red-200 cursor-pointer shadow-sm shadow-red-100"
                                    : "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100 cursor-pointer"
                                }
                              `}
                              data-testid={`slotgrid-${slot}`}
                            >
                              {slot}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Tables Tab (only for restaurants/pubs) */}
            {isRestaurant && (
              <TabsContent value="tables">
                <div className="space-y-6">
                  <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                    <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("tables.setup")}</h3>
                    <p className="text-sm text-slate-500 mb-4">{t("tables.setup.desc")}</p>
                    <div className="flex items-center gap-4">
                      <Input type="number" value={newTotalTables} onChange={(e) => setNewTotalTables(parseInt(e.target.value) || 0)} min={1} max={100} className="rounded-xl border-orange-200 w-32" data-testid="tables-count-input" />
                      <Button onClick={setupTables} disabled={savingTables || newTotalTables < 1 || newTotalTables > 100} className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-200 border-0" data-testid="setup-tables-btn">
                        {savingTables ? t("common.loading") : t("tables.setup")}
                      </Button>
                    </div>
                  </div>
                  {totalTables > 0 ? (
                    <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xs uppercase tracking-widest text-slate-400">
                          {t("tables.title")} ({tables.filter(t => t.status === "available").length}/{totalTables} {t("tables.available").toLowerCase()})
                        </h3>
                        <div className="flex items-center gap-4 text-xs">
                          <span className="flex items-center gap-1.5">
                            <span className="w-3 h-3 bg-emerald-400 border border-emerald-500 rounded-full" />
                            {t("tables.available")}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <span className="w-3 h-3 bg-red-400 border border-red-500 rounded-full" />
                            {t("tables.occupied")}
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-3">
                        {tables.map((table) => {
                          const isAvailable = table.status === "available";
                          const seats = table.seats || 2;
                          return (
                            <div key={table.table_number} className={`flex flex-col items-center border-2 rounded-xl p-2 transition-all duration-200 ${isAvailable ? "bg-emerald-50 border-emerald-400" : "bg-red-50 border-red-400"}`} data-testid={`table-${table.table_number}`}>
                              <button onClick={() => toggleTableStatus(table.table_number, table.status)} className="w-full flex flex-col items-center justify-center py-1 hover:opacity-80" title={isAvailable ? t("tables.occupy") : t("tables.free")}>
                                <span className={`font-bold text-lg ${isAvailable ? "text-emerald-700" : "text-red-700"}`}>{table.table_number}</span>
                                {table.freed_at && isAvailable && (
                                  <span className="text-[9px] text-emerald-600">
                                    {new Date(table.freed_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                                  </span>
                                )}
                              </button>
                              <div className="flex items-center gap-1 mt-1 border-t border-slate-200 pt-1 w-full justify-center">
                                <Users className="h-3 w-3 text-slate-400" />
                                <select
                                  value={seats}
                                  onChange={(e) => updateTableSeats(table.table_number, parseInt(e.target.value))}
                                  className="text-[11px] bg-transparent border-0 p-0 text-center font-medium text-slate-600 cursor-pointer"
                                  data-testid={`table-seats-${table.table_number}`}
                                >
                                  {[1,2,3,4,5,6,7,8,10,12].map(n => (
                                    <option key={n} value={n}>{n} posti</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-12 text-center">
                      <UtensilsCrossed className="h-16 w-16 text-orange-200 mx-auto mb-4" />
                      <p className="text-slate-400">{t("tables.empty")}</p>
                    </div>
                  )}
                </div>
              </TabsContent>
            )}

            {/* Menu Tab (for pizzerias/restaurants/bars) */}
            {hasMenu && (
              <TabsContent value="menu">
                <div className="space-y-6">
                  <div className="bg-amber-50 rounded-2xl border border-amber-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Percent className="h-5 w-5 text-amber-600" />
                      <span className="text-xs uppercase tracking-widest text-amber-800 font-bold">{t("menu.discount")}</span>
                    </div>
                    <p className="text-xs text-amber-700 mb-4">{t("menu.discount.desc")}</p>
                    <div className="flex items-center gap-4">
                      <Input type="number" value={dailyDiscount} onChange={(e) => setDailyDiscount(parseInt(e.target.value) || 0)} min={0} max={50} className="rounded-xl border-amber-300 w-[100px] bg-white" data-testid="daily-discount-input" />
                      <span className="text-amber-800">%</span>
                      <Button onClick={saveDailyDiscount} className="rounded-xl bg-amber-500 text-white hover:bg-amber-600 border-0" data-testid="save-discount-btn">Applica</Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                      <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("menu.add")}</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">{t("menu.name")} *</label>
                          <Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} placeholder="es. Pizza Margherita" className="rounded-xl border-orange-200" data-testid="menu-item-name" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400 mb-1 block">{t("menu.description")}</label>
                          <Input value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} placeholder="es. Pomodoro, mozzarella, basilico" className="rounded-xl border-orange-200" data-testid="menu-item-desc" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">{t("menu.price")} * (EUR)</label>
                            <Input type="number" step="0.50" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value) || 0 })} min={0} className="rounded-xl border-orange-200" data-testid="menu-item-price" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400 mb-1 block">{t("menu.category")}</label>
                            <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} className="w-full h-10 border border-orange-200 rounded-xl px-3 text-sm bg-white" data-testid="menu-item-category">
                              <option value="pizza">Pizza</option>
                              <option value="primo">Primo</option>
                              <option value="secondo">Secondo</option>
                              <option value="contorno">Contorno</option>
                              <option value="dolce">Dolce</option>
                              <option value="bevanda">Bevanda</option>
                              <option value="altro">Altro</option>
                            </select>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" id="daily-special" checked={newItem.is_daily_special} onChange={(e) => setNewItem({ ...newItem, is_daily_special: e.target.checked })} className="w-4 h-4 accent-amber-500" data-testid="menu-item-special" />
                          <label htmlFor="daily-special" className="text-sm text-slate-500">{t("menu.daily_special")} (sconto {dailyDiscount}%)</label>
                        </div>
                        <Button onClick={addMenuItem} disabled={addingItem || !newItem.name || newItem.price <= 0} className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:shadow-lg hover:shadow-orange-200 border-0 w-full" data-testid="add-menu-item-btn">
                          <Plus className="h-4 w-4 mr-2" />
                          {addingItem ? t("common.loading") : t("menu.add")}
                        </Button>
                      </div>
                    </div>
                    <div className="bg-white rounded-2xl border border-orange-100 shadow-sm p-6">
                      <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("menu.title")} ({menuItems.length})</h3>
                      {menuItems.length === 0 ? (
                        <div className="text-center py-8">
                          <Pizza className="h-12 w-12 text-orange-200 mx-auto mb-3" />
                          <p className="text-sm text-slate-400">{t("menu.empty")}</p>
                        </div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {menuItems.map((item) => (
                            <div key={item.item_id} className={`p-4 border rounded-xl ${item.is_daily_special ? "border-amber-300 bg-amber-50" : "border-orange-100"} hover:border-orange-300 transition-colors`} data-testid={`menu-item-${item.item_id}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-bold text-sm text-slate-800">{item.name}</p>
                                    {item.is_daily_special && <Badge className="rounded-full text-[10px] px-2 bg-amber-100 text-amber-700 border-amber-300">-{dailyDiscount}%</Badge>}
                                  </div>
                                  {item.description && <p className="text-xs text-slate-400 mb-1">{item.description}</p>}
                                  <p className="text-sm font-bold text-orange-600">
                                    {item.is_daily_special && dailyDiscount > 0 ? (
                                      <><span className="line-through text-slate-300 mr-2">{item.price.toFixed(2)}€</span>{(item.price * (1 - dailyDiscount / 100)).toFixed(2)}€</>
                                    ) : `${item.price.toFixed(2)}€`}
                                  </p>
                                </div>
                                <button onClick={() => deleteMenuItem(item.item_id)} className="p-1.5 hover:bg-red-50 transition-colors rounded-lg" data-testid={`delete-menu-${item.item_id}`}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>
            )}

            {/* Loyalty Tab */}
            <TabsContent value="loyalty">
              <div className="space-y-6">
                {/* Configure Loyalty Program */}
                <div className="bg-white border border-orange-100 rounded-2xl shadow-sm p-6">
                  <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">Programma Fedelta</h3>
                  <p className="text-sm text-slate-500 mb-4">Premia i tuoi clienti abituali. Dopo un certo numero di prenotazioni/acquisti, offri un premio!</p>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium">Attiva Programma</label>
                      <Switch checked={loyaltyEnabled} onCheckedChange={setLoyaltyEnabled} data-testid="loyalty-toggle" />
                    </div>
                    {loyaltyEnabled && (
                      <>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Quante prenotazioni per il premio?</label>
                          <Input type="number" value={loyaltyThreshold} onChange={e => setLoyaltyThreshold(parseInt(e.target.value) || 1)} min={1} max={100} className="rounded-xl border-orange-200 w-32" data-testid="loyalty-threshold" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Tipo di premio</label>
                          <select value={loyaltyRewardType} onChange={e => setLoyaltyRewardType(e.target.value)} className="rounded-xl border border-orange-200 px-4 py-2 text-sm" data-testid="loyalty-reward-type">
                            <option value="free_item">Articolo gratis (es. pizza, taglio...)</option>
                            <option value="discount_percent">Sconto percentuale (%)</option>
                            <option value="discount_fixed">Sconto fisso (€)</option>
                          </select>
                        </div>
                        {loyaltyRewardType !== "free_item" && (
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">{loyaltyRewardType === "discount_percent" ? "Percentuale sconto (%)" : "Importo sconto (€)"}</label>
                            <Input type="number" value={loyaltyRewardValue} onChange={e => setLoyaltyRewardValue(parseFloat(e.target.value) || 0)} min={0} className="rounded-xl border-orange-200 w-32" data-testid="loyalty-reward-value" />
                          </div>
                        )}
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Descrizione premio (es. "Pizza Margherita gratis", "10% sul prossimo ordine")</label>
                          <Input value={loyaltyRewardDesc} onChange={e => setLoyaltyRewardDesc(e.target.value)} placeholder="Es: Pizza Margherita gratis" className="rounded-xl border-orange-200" data-testid="loyalty-reward-desc" />
                        </div>
                      </>
                    )}
                    <Button onClick={saveLoyalty} disabled={savingLoyalty} className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-white btn-float" data-testid="save-loyalty-btn">
                      {savingLoyalty ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
                      Salva Programma Fedelta
                    </Button>
                  </div>
                </div>

                {/* Loyalty Customers List */}
                {loyaltyCustomers.length > 0 && (
                  <div className="bg-white border border-orange-100 rounded-2xl shadow-sm p-6">
                    <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-4">Clienti con Punti Fedelta</h3>
                    <div className="space-y-2">
                      {loyaltyCustomers.map(c => (
                        <div key={c.customer_id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${c.has_reward ? "bg-amber-500" : "bg-slate-400"}`}>
                            {c.name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{c.name}</p>
                            <p className="text-xs text-slate-500">{c.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-orange-600">{c.points}</p>
                            <p className="text-[10px] text-slate-400">/{loyaltyThreshold}</p>
                          </div>
                          {c.has_reward && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]">PREMIO</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

          </Tabs>
        </div>
      </div>

      {/* Booking Chat Dialog */}
      <BookingChat
        booking={chatBooking}
        isOpen={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setChatBooking(null);
        }}
        currentUserType="business"
      />
      <SupportChatButton />
    </div>
  );
}
